/**
 * Traducción de términos técnicos Lutron inglés → español.
 * Pasa las descripciones Lutron del Excel a un español legible para clientes.
 *
 * Reglas:
 *  - Hace replacement de frases enteras primero (para no romper composición)
 *  - Luego términos individuales
 *  - Preserva SKUs, voltajes (120V), unidades (W, A), nombres de línea (Caseta, etc.)
 */

const PHRASE_MAP: Array<[RegExp, string]> = [
  // Frases comunes — orden importa, más específico primero
  [/in-wall smart dimmer/gi, 'Atenuador inteligente de pared'],
  [/in-wall smart switch/gi, 'Switch inteligente de pared'],
  [/smart dimmer switch/gi, 'Atenuador inteligente'],
  [/smart switch for caseta/gi, 'Switch inteligente para Caseta'],
  [/smart dimmer for caseta/gi, 'Atenuador inteligente para Caseta'],
  [/wireless dimmer/gi, 'Atenuador inalámbrico'],
  [/wireless switch/gi, 'Switch inalámbrico'],
  [/single-pole\/3-way/gi, 'Polo simple / 3 vías'],
  [/single pole\/3-way/gi, 'Polo simple / 3 vías'],
  [/single-pole/gi, 'Polo simple'],
  [/single pole/gi, 'Polo simple'],
  [/multi-location/gi, 'Multi-ubicación'],
  [/3-way/gi, '3 vías'],
  [/4-way/gi, '4 vías'],
  [/neutral required/gi, 'Requiere neutro'],
  [/no neutral required/gi, 'Sin neutro requerido'],
  [/no neutral/gi, 'Sin neutro'],
  [/with neutral/gi, 'Con neutro'],
  [/forward phase/gi, 'Fase directa'],
  [/reverse phase/gi, 'Fase invertida'],
  [/quiet fan speed control/gi, 'Control silencioso de velocidad de ventilador'],
  [/fan speed control/gi, 'Control de velocidad de ventilador'],
  [/fan control/gi, 'Control de ventilador'],
  [/exhaust fan control/gi, 'Control de extractor'],
  [/smart bridge/gi, 'Smart Bridge'],
  [/smart hub/gi, 'Smart Hub'],
  [/wireless wall plate/gi, 'Tapa de pared inalámbrica'],
  [/wall plate/gi, 'Tapa de pared'],
  [/wallplate/gi, 'Tapa de pared'],
  [/wallbox/gi, 'Caja de pared'],
  [/with wallplate/gi, 'con tapa'],
  [/no wallplate/gi, 'sin tapa'],
  [/no insert/gi, 'sin inserto'],
  [/with insert/gi, 'con inserto'],
  [/companion switch/gi, 'Switch compañero'],
  [/raise\/lower/gi, 'Subir / Bajar'],
  [/scene keypad/gi, 'Keypad de escenas'],
  [/scene control/gi, 'Control de escenas'],
  [/main repeater/gi, 'Repetidor principal'],
  [/auxiliary repeater/gi, 'Repetidor auxiliar'],
  [/occupancy sensor/gi, 'Sensor de ocupación'],
  [/vacancy sensor/gi, 'Sensor de vacancia'],
  [/daylight sensor/gi, 'Sensor de luz natural'],
  [/wired sensor/gi, 'Sensor cableado'],
  [/wireless sensor/gi, 'Sensor inalámbrico'],
  [/dimming module/gi, 'Módulo de atenuación'],
  [/power pack/gi, 'Fuente de alimentación'],
  [/power module/gi, 'Módulo de potencia'],
  [/touch dimmer/gi, 'Atenuador táctil'],
  [/touch switch/gi, 'Switch táctil'],
  [/touch keypad/gi, 'Keypad táctil'],
  [/precision-molded/gi, 'precisión moldeada'],
  [/clear connect/gi, 'Clear Connect'],
  [/type x/gi, 'Type X'],
  [/lighting control/gi, 'Control de iluminación'],
  [/load type/gi, 'Tipo de carga'],
  [/dimmable led/gi, 'LED atenuable'],
  [/incandescent\/halogen/gi, 'Incandescente/Halógeno'],
  [/cfl\/led/gi, 'CFL/LED'],
  [/with engraving/gi, 'con grabado'],
  [/custom engraving/gi, 'grabado personalizado'],
];

const WORD_MAP: Record<string, string> = {
  // Adjetivos / acciones
  'dimmer': 'atenuador',
  'dimmers': 'atenuadores',
  'switch': 'switch',
  'switches': 'switches',
  'keypad': 'keypad',
  'keypads': 'keypads',
  'remote': 'control',
  'wireless': 'inalámbrico',
  'wired': 'cableado',
  'lighting': 'iluminación',
  'control': 'control',
  'controls': 'controles',
  'system': 'sistema',
  'systems': 'sistemas',
  'load': 'carga',
  'loads': 'cargas',
  'compatible': 'compatible',
  'with': 'con',
  'without': 'sin',
  'and': 'y',
  'or': 'o',
  'for': 'para',
  'requires': 'requiere',
  'required': 'requerido',
  'optional': 'opcional',
  'standard': 'estándar',
  'professional': 'profesional',
  'commercial': 'comercial',
  'residential': 'residencial',
  'white': 'blanco',
  'black': 'negro',
  'brown': 'café',
  'gray': 'gris',
  'almond': 'almendra',
  'ivory': 'marfil',
  'satin': 'satín',
  'gloss': 'gloss',
  'bronze': 'bronce',
  'nickel': 'níquel',
  'chrome': 'cromo',
  'brass': 'latón',
  'mounted': 'montado',
  'mount': 'montar',
  'installation': 'instalación',
  'wall': 'pared',
  'ceiling': 'techo',
  'output': 'salida',
  'input': 'entrada',
  'voltage': 'voltaje',
  'power': 'potencia',
  'battery': 'batería',
  'rechargeable': 'recargable',
  'kit': 'kit',
  'package': 'paquete',
  'paddle': 'palanca',
  'tap': 'tap',
};

/**
 * Traduce una descripción Lutron del inglés al español preservando SKUs,
 * voltajes, números y términos que no deben cambiar.
 */
export function translateLutronDesc(text: string): string {
  if (!text) return text;
  let out = text;

  // 1) Frases completas primero
  for (const [pattern, replacement] of PHRASE_MAP) {
    out = out.replace(pattern, replacement);
  }

  // 2) Palabras individuales (sin tocar las que ya tienen mayúsculas iniciales preservadas)
  out = out.replace(/\b([a-zA-Z]+)\b/g, (match) => {
    const lower = match.toLowerCase();
    // Preservar siglas y palabras que NO deben traducir
    const PRESERVE = new Set([
      'caseta','radiora','homeworks','maestro','vive','sunnata','palladiom','alisse',
      'diva','claro','sivoia','triathlon','pico','lutron','athena','lumaris','seetouch',
      'qsx','qs','ra3','ra2','rf','led','elv','mlv','cfl','dali','bacnet','bms','wifi',
      'on','off','120v','230v','277v','dim','triac','phase',
    ]);
    if (PRESERVE.has(lower)) return match;
    if (WORD_MAP[lower]) {
      // Preservar capitalización inicial si la tenía
      const translated = WORD_MAP[lower];
      if (match[0] === match[0].toUpperCase() && match.length > 1) {
        return translated[0].toUpperCase() + translated.slice(1);
      }
      return translated;
    }
    return match;
  });

  return out;
}

/**
 * Genera un nombre amigable a partir del nombre corto del Excel + brand.
 * "CASETA 250W LED 3WY" → "Caseta · Atenuador 250W LED 3 vías"
 */
export function prettifyLutronName(rawName: string): string {
  if (!rawName) return rawName;
  let n = rawName.trim();
  // Title case primero
  n = n.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.substr(1).toLowerCase());
  // Reemplazos comunes
  n = n.replace(/\bSwty\b/g, 'Switch');
  n = n.replace(/\bSwch\b/g, 'Switch');
  n = n.replace(/\b3wy\b/gi, '3 vías');
  n = n.replace(/\b4wy\b/gi, '4 vías');
  n = n.replace(/\bMult\b/gi, 'Multi-ubicación');
  n = n.replace(/\bMlti\b/gi, 'Multi-ubicación');
  n = n.replace(/\bDim\b/gi, 'Atenuador');
  n = n.replace(/\bDimr\b/gi, 'Atenuador');
  n = n.replace(/\bSwtch\b/gi, 'Switch');
  n = n.replace(/\bKp\b/gi, 'Keypad');
  n = n.replace(/\bWp\b/gi, 'Tapa');
  n = n.replace(/\bNtrl\b/gi, 'c/Neutro');
  n = n.replace(/\bW\/Ntrl\b/gi, 'c/Neutro');
  n = n.replace(/\bWith Insrt\b/gi, 'con inserto');
  n = n.replace(/\bNoinsrt\b/gi, 'sin inserto');
  n = n.replace(/\bInsrt\b/gi, 'inserto');
  return n;
}
